import React, { useState } from 'react';
import Tooltip from './Tooltip';
import { Teacher } from '../types';
import SmartcareSignInButton from './SmartcareSignInButton';
import ClassroomTransferButton from './ClassroomTransferButton';
import TeacherSearchDialog from './TeacherSearchDialog';
import './TeacherTooltip.css';
import TooltipComponent from './TooltipComponent';

interface TeacherTooltipProps {
  teachers: Teacher[];
  classroomId?: string;
  position?: 'top' | 'right' | 'bottom' | 'left';
}

const TeacherTooltip: React.FC<TeacherTooltipProps> = ({ 
  teachers, 
  classroomId,
  position = 'bottom' 
}) => {
  const [isSearchDialogOpen, setIsSearchDialogOpen] = useState(false);

  // Check if any teacher needs to sign into SmartCare
  const hasTeachersNotSignedIn = teachers.some(teacher => teacher.sc_id && !teacher.sc_checkedin);
  // Check if any teacher is missing SmartCare ID
  const hasTeachersMissingId = teachers.some(teacher => !teacher.sc_id);
  // Check if any teacher is not clocked into WhenIWork
  const hasTeachersNotClockedIn = teachers.some(teacher => teacher.wiw_id && !teacher.wiw_clockedin);
  
  // Count teachers not on break
  const activeTeachersCount = teachers.filter(teacher => !teacher.onBreak).length;

  const handleSearchClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent tooltip from closing
    setIsSearchDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsSearchDialogOpen(false);
  };

  const renderTeacherTooltipContent = () => (
    <div className="tooltip-section">
      <div className="tooltip-section-title">
        <div className="tooltip-header-with-icon">
          <span>Teachers ({teachers.length})</span>
          {classroomId && (
            <span 
              className="add-teacher-icon" 
              title="Add a teacher to this classroom"
              onClick={handleSearchClick}
            />
          )}
        </div>
      </div>
      {teachers.length > 0 ? (
        <ul className="tooltip-name-list">
          {teachers.map((teacher, index) => (
            <li key={index} className={teacher.onBreak ? 'on-break' : ''}>
              <div className="teacher-list-item">
                <div className="teacher-info">
                  {teacher.name}
                  {teacher.smartcareLocation && teacher.smartcareLocation !== classroomId && 
                    ` (${teacher.smartcareLocation})`
                  }
                  {teacher.onBreak && ' (on break)'}
                  {teacher.sc_id && !teacher.sc_checkedin && 
                    <SmartcareSignInButton teacher={teacher} currentClassroomId={classroomId} />
                  }
                  {!teacher.sc_id && 
                    <span className="missing-id-icon" title="Does not exist in Smartcare"></span>
                  }
                  {teacher.wiw_id && !teacher.wiw_clockedin && 
                    <span className="not-clocked-icon" title="Teacher is not clocked in to WhenIWork"></span>
                  }
                  {teacher.wiw_shift && (
                    <TooltipComponent 
                      text={`🕛`}
                      tooltipText={`${new Date(teacher.wiw_shift.start_time).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})} - ${new Date(teacher.wiw_shift.end_time).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}`}                    />

                  )}
                </div>
                {classroomId && (
                  <div className="transfer-button-container">
                    <ClassroomTransferButton 
                      teacher={teacher} 
                      currentClassroomId={classroomId} 
                    />
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <div className="tooltip-empty">No teachers assigned</div>
      )}
      
      {(hasTeachersNotSignedIn || hasTeachersMissingId || hasTeachersNotClockedIn) && (
        <div className="tooltip-legend">
          {hasTeachersNotSignedIn && (
            <>
              <span className="signin-icon-button"></span>
              <span className="legend-text">Click to sign user into Smartcare</span>
            </>
          )}
          {(hasTeachersNotSignedIn && (hasTeachersMissingId || hasTeachersNotClockedIn)) && <br />}
          {hasTeachersMissingId && (
            <>
              <span className="missing-id-icon"></span>
              <span className="legend-text">Does not exist in Smartcare</span>
            </>
          )}
          {(hasTeachersMissingId && hasTeachersNotClockedIn) && <br />}
          {hasTeachersNotClockedIn && (
            <>
              <span className="not-clocked-icon"></span>
              <span className="legend-text">Teacher is not clocked in to WhenIWork</span>
            </>
          )}
        </div>
      )}
    </div>
  );

  return (
    <span className="teacher-tooltip-component">
      <Tooltip content={renderTeacherTooltipContent()} position={position}>
          <span className="count-with-tooltip">
              {activeTeachersCount}
              {teachers.some(teacher => 
              (teacher.smartcareLocation && teacher.smartcareLocation !== classroomId) ||
              !teacher.sc_id ||
              !teacher.sc_checkedin ||
              (teacher.wiw_id && !teacher.wiw_clockedin)
              ) && <span className="location-mismatch-icon" title="Teacher assigned to different location"></span>}
          </span>
      </Tooltip>
      
      {isSearchDialogOpen && classroomId && (
        <TeacherSearchDialog 
          onClose={handleCloseDialog} 
          targetClassroomId={classroomId} 
        />
      )}
    </span>
  );
};

export default TeacherTooltip;
